import { CustomPropertyWrapperColumn, Column } from "./columns";
import { EMPTY_ROW_FLAG, fail, isStringType } from "./common";
import { FlatStringStorage } from "../../../wgs/scene/InstanceTreeStorage";
import { PROPERTY_ACTIONS } from "../../main/WorkerActions";
import { AttributeType } from "../../lmvtk/common/PropdbEnums";
export class ReadonlyFilterTableImpl {
    #EMPTY_ROW;
    get rowCount() {
        return this.#ids.length / 2;
    }
    get columnCount() {
        return this.#attributes.length;
    }
    get rowDefinition() {
        return this.#attributes;
    }
    #attributes;
    #ids;
    #idsView;
    // Indexes the IDs array for custom sorting
    #idSearchIndex;
    #columns;
    #chunkStride;
    #stringStorage;
    #idsSorted = false;
    constructor(descriptor) {
        this.#initializeFromDescriptor(descriptor);
    }
    // Needed to re-initialize when sorting in worker
    #initializeFromDescriptor(descriptor) {
        this.#ids = descriptor.ids;
        this.#idsView = new DataView(this.#ids.buffer);
        this.#idSearchIndex = descriptor.idSearchIndex;
        this.#idsSorted = !!descriptor.idsSorted;
        this.#chunkStride = descriptor.stride;
        const { attributes, columns } = descriptor;
        this.#attributes = attributes;
        const storage = this.#stringStorage = new FlatStringStorage(descriptor.strings);
        // Check for no columns
        this.#EMPTY_ROW = (new Array(this.#attributes.length)).fill(undefined);
        const hasNoData = !columns || columns.length === 0;
        if (hasNoData) {
            return;
        }
        const attibutesByHash = new Map();
        for (const a of attributes) {
            if (!a)
                continue; //empty colunm
            attibutesByHash.set(a.propertyHash, a);
        }
        const sCustomAttributes = new Set(descriptor.attributes.filter(it => it && it?.propType?.toLowerCase() === 'custom').map(prop => prop.propertyHash));
        // instantiate one column instance for instance of attribute
        const columnsByHash = new Map();
        for (const column of columns) {
            const isCustom = sCustomAttributes.has(column.hash);
            const dictionariesTable = column.dictionaries;
            columnsByHash.set(column.hash, column.descriptors.map((descriptor, index) => {
                let dictionary;
                if (Array.isArray(dictionariesTable) && dictionariesTable[index]) {
                    dictionary = new FlatStringStorage(dictionariesTable[index]);
                }
                else if (dictionariesTable) {
                    dictionary = new FlatStringStorage(dictionariesTable);
                }
                else {
                    dictionary = storage; // global storage fallback
                }
                const chunk = Column.fromWireType(this.#ids.length / 2, attibutesByHash.get(column.hash), descriptor, dictionary);
                return isCustom ? CustomPropertyWrapperColumn.wrap(chunk) : chunk;
            }));
        }
        this.#columns = attributes.map(a => columnsByHash.get(a?.propertyHash));
    }
    idAt(index) {
        return this.#ids[index * 2];
    }
    containsObjectId(objectId) {
        return this.indexOf(objectId) >= 0;
    }
    /**
     * If this table has an ID index, will use that. Otherwise will perform a linear scan
     * @param {number} objectId
     */
    indexOf(objectId) {
        if (objectId < 0)
            return -1;
        if (this.#idSearchIndex) {
            return this.#binarySearch(objectId, this.#idSearchIndex);
        }
        else if (this.#idsSorted) {
            // Binary search directly on sorted IDs array
            let left = 0;
            let right = this.rowCount - 1;
            while (left <= right) {
                const mid = Math.floor((left + right) / 2);
                const midId = this.#ids[mid * 2];
                if (midId === objectId) {
                    return mid; // Return the row index
                }
                else if (midId < objectId) {
                    left = mid + 1;
                }
                else {
                    right = mid - 1;
                }
            }
            return -1;
        }
        else {
            let index = -1;
            this.enumIds((id, i) => {
                if (id === objectId) {
                    index = i;
                    return true;
                }
                return false;
            });
            return index;
        }
    }
    getRowById(objectId) {
        return this.getRow(this.indexOf(objectId));
    }
    getRow(index) {
        if (index < 0 || index >= this.rowCount)
            return null;
        if (!this.#columns)
            return this.#EMPTY_ROW;
        // Check for empty row flag
        if (this.isEmpty(index))
            return this.#EMPTY_ROW;
        index = this.#ids[index * 2 + 1]; // map to internal index
        const chunkIndex = Math.floor(index / this.#chunkStride);
        const subIndex = index % this.#chunkStride;
        const result = new Array(this.#attributes.length);
        for (let i = 0; i < result.length; i++) {
            const column = this.#columns[i];
            if (!column) {
                result[i] = undefined;
            }
            else {
                const colChunk = column[chunkIndex];
                result[i] = colChunk.getValueAt(subIndex);
            }
        }
        return result;
    }
    getValueAt(rowIndex, columnIndex) {
        if (rowIndex < 0 || rowIndex >= this.rowCount)
            return undefined;
        if (!this.#columns)
            return this.#EMPTY_ROW;
        // Check for empty row flag
        if (this.isEmpty(rowIndex))
            return undefined;
        rowIndex = this.#ids[rowIndex * 2 + 1]; // map to internal index
        const column = this.#columns[columnIndex];
        if (!column) {
            return undefined;
        }
        else {
            const chunkIndex = Math.floor(rowIndex / this.#chunkStride);
            const subIndex = rowIndex % this.#chunkStride;
            const colChunk = column[chunkIndex];
            return colChunk?.getValueAt(subIndex);
        }
    }
    async orderByAsync(propertyDb, configuration) {
        if (!configuration.length)
            return;
        if (typeof window !== 'undefined' && self === window) {
            const result = await new Promise((res, rej) => {
                const descriptor = this.toWireType();
                const buffers = descriptor.buffers;
                delete descriptor.buffers;
                propertyDb.asyncPropertyOperation({
                    operation: PROPERTY_ACTIONS.SORT_FILTER_TABLE,
                    descriptor,
                    configuration
                }, res, rej, () => null, buffers);
            });
            this.#initializeFromDescriptor(result.descriptor);
            this.#idsSorted = false;
            return;
        }
        const sortingConfig = configuration.map(config => {
            if (typeof config.propertyHash === 'string') {
                return { attr: config, descending: false };
            }
            else {
                return config;
            }
        });
        const configToColIdx = new Map(sortingConfig.map(config => [
            config,
            this.#attributes.indexOf(this.#attributes.find(attr => attr.propertyHash === config.attr.propertyHash)
                ?? fail(`No such attribute ${JSON.stringify(config ?? '')}`))
        ]));
        // is inside worker thread
        this.#sortInternal((aId, aIdx, bId, bIdx) => {
            for (const config of sortingConfig) {
                const colIdx = configToColIdx.get(config);
                const aVal = config.descending ? this.#getColumnValue(colIdx, bIdx) : this.#getColumnValue(colIdx, aIdx);
                const bVal = config.descending ? this.#getColumnValue(colIdx, aIdx) : this.#getColumnValue(colIdx, bIdx);
                if (aVal === bVal)
                    continue;
                if (isStringType(config.attr)) {
                    return aVal
                        ? aVal?.localeCompare(bVal)
                        : (config.descending ? 1 : -1); // handles nulls & asc/desc
                }
                else {
                    return aVal - bVal;
                }
            }
            return 0;
        });
    }
    // For sorting when whole column doesn't have to be read
    #sortInternal(compareFn) {
        if (!compareFn)
            return this;
        const doubleSort = new BigUint64Array(this.#ids.buffer);
        const register = new BigUint64Array(1);
        const registerView = new DataView(register.buffer);
        doubleSort.sort((a, b) => {
            registerView.setBigUint64(0, a, true);
            const aId = registerView.getUint32(0, true);
            const aIndex = registerView.getUint32(4, true);
            registerView.setBigUint64(0, b, true);
            const bId = registerView.getUint32(0, true);
            const bIndex = registerView.getUint32(4, true);
            return compareFn(aId, aIndex, bId, bIndex);
        });
        return this;
    }
    #getColumnValue(columnIdx, rowIdx) {
        if (!this.#columns[columnIdx])
            return;
        const chunkIndex = Math.floor(rowIdx / this.#chunkStride);
        const subIndex = rowIdx % this.#chunkStride;
        const col = this.#columns[columnIdx][chunkIndex];
        return col.getValueAt(subIndex);
    }
    sort(compareFn) {
        if (!compareFn)
            return this;
        this.#idsSorted = false;
        const doubleSort = new BigUint64Array(this.#ids.buffer);
        const register = new BigUint64Array(1);
        const registerView = new DataView(register.buffer);
        doubleSort.sort((a, b) => {
            registerView.setBigUint64(0, a, true);
            const aId = registerView.getUint32(0, true);
            const aIndex = registerView.getUint32(4, true);
            registerView.setBigUint64(0, b, true);
            const bId = registerView.getUint32(0, true);
            const bIndex = registerView.getUint32(4, true);
            const rowA = this.getRow(aIndex);
            const rowB = this.getRow(bIndex);
            return compareFn([aId, rowA], [bId, rowB]);
        });
        this.#updateSearchIndex();
        return this;
    }
    /**
     * If this was the last sorting action called, will use IDs as binary search for `indexOf` calls
     */
    sortById() {
        this.#sortInternal((aId, _, bId) => aId - bId);
        this.#updateSearchIndex();
        this.#idsSorted = true;
    }
    enumIds(cb, rowsWithValuesOnly = false) {
        for (let i = 0; i < this.rowCount; i++) {
            if (rowsWithValuesOnly && this.isEmpty(i))
                continue;
            if (cb(this.#ids[i * 2], i))
                return;
        }
    }
    searchText(searchTerm, includeColumns) {
        if (typeof searchTerm === 'number' || typeof searchTerm === 'boolean') { // not documented but supported
            searchTerm = String(searchTerm);
        }
        if (typeof searchTerm !== 'string' || !searchTerm?.length) {
            return {
                cancel: () => null,
                result: Promise.resolve(null)
            };
        }
        if (!includeColumns) {
            // if not specified, include all columns.
            includeColumns = this.#attributes.map(a => a.propertyHash);
        }
        let includeBooleans = true;
        let includeNumbers = true;
        // also cheat and check the search term if we can exclude numeric columns and/or boolean columns
        if (searchTerm !== 'true' && searchTerm !== 'false')
            includeBooleans = false;
        if (!/^-?\d+(\.\d+)?$/.test(searchTerm))
            includeNumbers = false;
        const attributeByHash = new Map(this.#attributes.map(a => [a.propertyHash, a]));
        // in case non-existent columns passed from input
        includeColumns = includeColumns.filter(hash => attributeByHash.has(hash));
        const typeofAttribute = (hash) => attributeByHash.get(hash).dataType;
        includeColumns = includeColumns.filter(hash => {
            switch (typeofAttribute(hash)) {
                case AttributeType.Boolean: return includeBooleans;
                case AttributeType.Float: return includeNumbers;
                case AttributeType.Double: return includeNumbers;
                case AttributeType.Integer: return includeNumbers;
                case AttributeType.DbKey: return includeNumbers;
                default: return isStringType(attributeByHash.get(hash));
            }
        });
        // we really need to get Promise.withResolvers type...
        let resolver = () => null;
        const promisedResult = new Promise((resolve) => {
            resolver = resolve;
        });
        const finalColumns = includeColumns.map(hash => this.#columns[this.#attributes.indexOf(attributeByHash.get(hash))]);
        const cancelCallback = this.#searchTextInterruptible(searchTerm, resolver, finalColumns);
        return {
            cancel: cancelCallback,
            result: promisedResult
        };
    }
    /**
     * @param {string} searchTerm
     * @param resolver Called when the text search is complete
     * @param columns
     * @returns {() => void} Callback for cancelling the search (e.g. for debouncing)
     */
    #searchTextInterruptible(searchTerm, resolver, columns) {
        // Possibly temporary requirement: always going to be a case-insensitive search
        searchTerm = searchTerm.toLowerCase();
        const predicate = (value) => value.toLowerCase().indexOf(searchTerm) >= 0;
        let CANCEL_TRAP = false;
        const MS_TIMESLICE = 10;
        // dynamically set string reads for interrupts
        let stringReadsInterrupt = 1_000_000;
        // Start search after returning the cancellation callback, setTimeout to continue control flow
        setTimeout(async () => {
            const result = new Map();
            const matchingValues = new Map();
            let lastInterrupt = Date.now();
            for (let i = 0; i < this.#stringStorage.next; i++) {
                const value = this.#stringStorage.get(i);
                if (value && predicate(value)) {
                    matchingValues.set(i, value);
                }
                if (i > stringReadsInterrupt) {
                    const now = Date.now();
                    // Increase string reads target, we didn't read enough to fill 10ms
                    if (now - lastInterrupt < MS_TIMESLICE) {
                        stringReadsInterrupt = Math.round(stringReadsInterrupt * 1.5);
                    }
                    else { // Decrease string reads target
                        stringReadsInterrupt = Math.round(stringReadsInterrupt * 0.75);
                        lastInterrupt = now;
                        await interrupt(0);
                        // This happens if a debounce is triggered and the search gets cancelled
                        if (CANCEL_TRAP) {
                            resolver(null);
                            return;
                        }
                    }
                }
            }
            const searchColumnAttributes = columns.map(col => this.#attributes[this.#columns.indexOf(col)]);
            for (let i = 0; i < columns.length; i++) {
                const attr = searchColumnAttributes[i];
                const resultsForProperty = new Map();
                for (let j = 0; j < columns[i].length; j++) {
                    for (let k = 0; k < columns[i][j].idCount; k++) {
                        const rowIndex = (j * this.#chunkStride) + k;
                        const id = this.idAt(rowIndex);
                        if (isStringType(attr)) {
                            const chunk = columns[i][j];
                            const valueId = chunk.valueIdAtIndex(k);
                            if (matchingValues.has(valueId)) {
                                const existing = resultsForProperty.get(valueId);
                                if (!existing) {
                                    resultsForProperty.set(valueId, [id]);
                                }
                                else {
                                    existing.push(id);
                                }
                            }
                        }
                        else { // numeric/boolean types
                            const chunk = columns[i][j];
                            const value = chunk.getValueAt(k);
                            if (value && predicate(`${value}`)) {
                                const existing = resultsForProperty.get(value) ?? [];
                                if (!existing.length)
                                    resultsForProperty.set(value, existing);
                                existing.push(id);
                            }
                        }
                    }
                    // Column chunks max out at several megabytes large,
                    // check for interrupt conditions again here
                    const now = Date.now();
                    if (now - lastInterrupt > MS_TIMESLICE) {
                        lastInterrupt = now;
                        await interrupt(0);
                        if (CANCEL_TRAP) {
                            resolver(null);
                            return;
                        }
                    }
                }
                if (isStringType(attr)) {
                    // remap results, only one string read per value match
                    const remappedMap = new Map();
                    for (const valueId of resultsForProperty.keys()) {
                        remappedMap.set(matchingValues.get(valueId), resultsForProperty.get(valueId) ?? []);
                        resultsForProperty.delete(valueId);
                    }
                    if (remappedMap.size > 0)
                        result.set(attr.propertyHash, remappedMap);
                }
                else {
                    resultsForProperty.size && result.set(attr.propertyHash, resultsForProperty);
                }
            }
            resolver(CANCEL_TRAP ? null : result);
        });
        return () => {
            CANCEL_TRAP = true;
        };
    }
    isEmpty(index) {
        return !!(this.#idsView.getUint8(index * 8 + 7) & EMPTY_ROW_FLAG);
    }
    isNull(rowIndex, columnIndex) {
        if (!this.#columns[columnIndex])
            return;
        if (rowIndex < 0 || rowIndex >= this.rowCount)
            return null;
        if (!this.#columns)
            return this.#EMPTY_ROW;
        if (this.isEmpty(rowIndex))
            return this.#EMPTY_ROW;
        rowIndex = this.#ids[rowIndex * 2 + 1]; // map to internal rowIndex
        const chunkIndex = Math.floor(rowIndex / this.#chunkStride);
        const subIndex = rowIndex % this.#chunkStride;
        const col = this.#columns[columnIndex][chunkIndex];
        return col.isNull(subIndex);
    }
    #updateSearchIndex() {
        if (!this.#idSearchIndex)
            return;
        for (let i = 0; i < this.#ids.length / 2; i++) {
            this.#idSearchIndex[i * 2] = this.#ids[i * 2];
            this.#idSearchIndex[i * 2 + 1] = i;
        }
        const doubleSort = new BigUint64Array(this.#idSearchIndex.buffer);
        const register = new BigUint64Array(1);
        const registerView = new DataView(register.buffer);
        doubleSort.sort((a, b) => {
            registerView.setBigUint64(0, a, true);
            const aId = registerView.getUint32(0, true);
            registerView.setBigUint64(0, b, true);
            const bId = registerView.getUint32(0, true);
            return aId - bId;
        });
    }
    #binarySearch(id, index) {
        let left = 0;
        let right = (index.length / 2) - 1;
        while (left <= right) {
            const mid = Math.floor((left + right) / 2);
            const midIndex = mid * 2;
            const midValue = index[midIndex];
            if (midValue === id) {
                return index[midIndex + 1];
            }
            else if (midValue < id) {
                left = mid + 1;
            }
            else {
                right = mid - 1;
            }
        }
        return -1;
    }
    /**
     * Debug function
     */
    _byteCount() {
        if (!this.#columns)
            return this.#EMPTY_ROW.length * 2;
        let byteCount = 0;
        byteCount += this.#stringStorage.buf.byteLength ?? 0;
        byteCount += this.#stringStorage.idx.byteLength ?? 0;
        for (const chunks of this.#columns.values()) {
            if (!chunks)
                continue;
            for (const column of chunks) {
                byteCount += column.byteCount();
            }
        }
        // Column chunks don't know locally who they share their dictionary table with
        const sDictionaries = new Set();
        this.#columns.forEach(col => col?.forEach(chunk => {
            if (!chunk)
                return;
            if (chunk.isStringType) {
                const dict = chunk.asDictionaryType()?.getDictionary();
                sDictionaries.add(dict?.buf?.buffer);
                sDictionaries.add(dict?.idx?.buffer);
            }
        }));
        sDictionaries.forEach((dict) => {
            byteCount += dict?.byteLength ?? 0;
        });
        // Include ids and index
        byteCount += this.#ids.byteLength;
        byteCount += this.#idSearchIndex?.byteLength ?? 0;
        return byteCount;
    }
    toWireType() {
        const { buf, idx, next } = this.#stringStorage;
        const uniqueHashes = new Set();
        this.#attributes.forEach(a => a && uniqueHashes.add(a.propertyHash));
        const hashToColumn = new Map();
        for (let i = 0; i < this.#attributes.length; i++) {
            const attr = this.#attributes[i];
            if (attr && !hashToColumn.has(attr.propertyHash)) {
                hashToColumn.set(attr.propertyHash, this.#columns[i]);
            }
        }
        const result = {
            attributes: this.#attributes,
            ids: this.#ids,
            idSearchIndex: this.#idSearchIndex,
            idsSorted: this.#idsSorted,
            columns: [...hashToColumn.entries()].map(([hash, colChunks]) => ({
                hash,
                descriptors: colChunks.map(it => it.toWireType()),
                dictionaries: isStringType(this.rowDefinition.find(it => it.propertyHash === hash))
                    ? colChunks.map(chunk => {
                        const dictionary = chunk.getDictionary();
                        dictionary?.flatten(); // No-op if already done
                        return dictionary;
                    })
                    : undefined
            })),
            stride: this.#chunkStride,
            strings: { buf, idx, next },
            buffers: []
        };
        result.buffers = result.columns.map(it => it.descriptors).flat(2).map(x => x.buffer).filter(it => it);
        // Special case: if no buffers after flattening columns, just make the columns an empty array
        // No columns but attributes present means it was a fully empty filter, but with IDs present
        if (result.buffers.length === 0) {
            result.columns = [];
        }
        // Dictionary buffers can be local, or be shared across many column chunks for non-PDB based indexing.
        // Or they can be global for the whole table. Collect them here
        const dictionaryBuffers = new Set();
        dictionaryBuffers.add(null);
        dictionaryBuffers.add(undefined);
        result.columns.forEach(col => col?.dictionaries?.forEach(descriptor => {
            // typescript not recognizing this...
            const bufs = result.buffers;
            if (!dictionaryBuffers.has(descriptor?.idx?.buffer)) {
                dictionaryBuffers.add(descriptor.idx.buffer);
                bufs.push(descriptor?.idx?.buffer);
            }
            if (!dictionaryBuffers.has(descriptor?.buf?.buffer)) {
                dictionaryBuffers.add(descriptor?.buf?.buffer);
                bufs.push(descriptor?.buf?.buffer);
            }
        }));
        !dictionaryBuffers.has(buf.buffer) && result.buffers.push(buf.buffer);
        !dictionaryBuffers.has(idx.buffer) && result.buffers.push(idx.buffer);
        result.buffers.push(result.ids.buffer);
        if (result.idSearchIndex) {
            result.buffers.push(result.idSearchIndex.buffer);
        }
        return result;
    }
    static getStorage(table) {
        return {
            columns: table.#columns,
            ids: table.#ids,
            storage: table.#stringStorage
        };
    }
}
async function interrupt(timeout) {
    await new Promise(res => {
        setTimeout(res, timeout);
    });
}
